import { ReactElement, ReactNode } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  Collapse,
  FormControlLabel,
  Switch,
  SxProps,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import CopyAllOutlined from '@mui/icons-material/CopyAllOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import PictureAsPdfOutlined from '@mui/icons-material/PictureAsPdfOutlined';
import { FormattedMessage, unsafeTranslationKey, formatTranslation } from 'intl';
import { ReportTranslationKeys } from 'intl/keys';
import { useIntl } from 'react-intl';
import { Color } from 'shared/utils/colors';
import {
  ImageDisplayStyle,
  ReportAdditionalInformationField,
} from 'shared/domain/report/types/model/common';
import { DisplayStyleSegmentedControl } from '../segmentedControls/displayStyle/presentational';
import { REPORT_FILE_INFO_CHIP_TOOLTIP_LABELS } from '../reportTooltipLabels';
import { ReportFilterChip } from './ReportFilterChip';
import {
  getContentDimOpacity,
  REPORT_CONTENT_LIST_SX,
  REPORT_CONTENT_SECTION_ICON_SIZE,
  REPORT_CONTENT_SUBOPTIONS_SX,
  REPORT_FORM_FULL_WIDTH_SX,
  REPORT_SUBOPTION_LABEL_SX,
  REPORT_SUBOPTION_ROW_SX,
  reportContentSwitchSx,
  reportListItemCollapseDetailsSx,
  SECTION_SUBHEADER_STYLE,
} from './styles';

export function ReportFormSectionSubheader({
  messageId,
}: {
  messageId: string;
}): ReactElement {
  return (
    <Typography component='div' sx={SECTION_SUBHEADER_STYLE}>
      <FormattedMessage id={unsafeTranslationKey(messageId)} />
    </Typography>
  );
}

export function ReportAdditionalContentEmptyInfo(): ReactElement {
  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}
      data-qa='report_additional_content_empty'
    >
      <InfoOutlined sx={{ fontSize: 18, color: 'text.disabled' }} />
      <Typography variant='body2' color='text.secondary'>
        <FormattedMessage id={ReportTranslationKeys.additionalContentEmpty} />
      </Typography>
    </Box>
  );
}

export function ReportSuboptionRow({
  labelId,
  children,
}: {
  labelId: string;
  children: ReactNode;
}): ReactElement {
  return (
    <Box sx={REPORT_SUBOPTION_ROW_SX}>
      <Typography component='div' sx={REPORT_SUBOPTION_LABEL_SX}>
        <FormattedMessage id={unsafeTranslationKey(labelId)} />
      </Typography>
      <Box sx={{ minWidth: 0, maxWidth: '100%', overflowX: 'auto' }}>{children}</Box>
    </Box>
  );
}

function wrapSwitchWithUnavailableTooltip(
  intl: ReturnType<typeof useIntl>,
  switchControl: ReactElement,
  unavailableReasonId?: string
): ReactElement {
  if (!unavailableReasonId) {
    return switchControl;
  }

  return (
    <Tooltip
      title={formatTranslation(intl, unsafeTranslationKey(unavailableReasonId) )}
      arrow
      placement='top'
    >
      <Box component='span' sx={{ display: 'inline-flex', flexShrink: 0 }}>
        {switchControl}
      </Box>
    </Tooltip>
  );
}

export function ReportContentListItem({
  icon,
  title,
  enabled,
  onEnabledChange,
  disabled,
  unavailable,
  unavailableReasonId,
  inlineControls,
  showSwitch = true,
  switchAriaLabelId,
  variant = 'default',
  contentVariant = 'default',
  'data-qa': dataQa,
  children,
}: {
  icon?: ReactNode;
  title: ReactNode;
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  disabled?: boolean;
  unavailable?: boolean;
  unavailableReasonId?: string;
  /** Shown between the label and switch when the row is enabled (e.g. link mode). */
  inlineControls?: ReactNode;
  /** When false, the row has no toggle and sub-options stay visible. */
  showSwitch?: boolean;
  /** Message id used as the switch accessible label when `title` is not a plain string. */
  switchAriaLabelId?: string;
  variant?: 'default' | 'nested';
  contentVariant?: 'default' | 'nested';
  'data-qa'?: string;
  children?: ReactNode;
}): ReactElement {
  const intl = useIntl();
  const isNested = variant === 'nested';
  const isNestedContent = contentVariant === 'nested';
  const hasDetails = children !== undefined && children !== null;
  const isUnavailable = unavailable === true;
  const switchChecked = showSwitch ? enabled && !isUnavailable : true;
  const showDetails = hasDetails && !isUnavailable;
  const isSwitchInteractive = !disabled && !isUnavailable;
  const switchAriaLabel =
    typeof title === 'string'
      ? title
      : switchAriaLabelId
        ? formatTranslation(intl, unsafeTranslationKey(switchAriaLabelId) )
        : undefined;

  return (
    <Box
      data-qa={dataQa}
      sx={{
        pl: isNested ? 0 : 2,
        pr: isNested ? 0 : 2,
        pb: isNested ? 0.5 : undefined,
        minWidth: 0,
        maxWidth: '100%',
        boxSizing: 'border-box',
        ...(isNested
          ? undefined
          : {
              '&:not(:last-of-type)': {
                borderBottom: 1,
                borderColor: Color.blackWithOpacity,
              },
            }),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          minHeight: isNested ? 32 : 40,
          py: isNested ? 0 : 0.5,
        }}
      >
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flex: 1,
            minWidth: 0,
            pr: 1,
            opacity: getContentDimOpacity(
              theme,
              isUnavailable,
              switchChecked
            ),
            transition: theme.transitions.create('opacity', {
              duration: theme.transitions.duration.shortest,
            }),
          })}
        >
          {icon ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: REPORT_CONTENT_SECTION_ICON_SIZE,
                height: REPORT_CONTENT_SECTION_ICON_SIZE,
                flexShrink: 0,
                color: isUnavailable ? 'text.disabled' : 'text.primary',
                '& .MuiSvgIcon-root': {
                  fontSize: REPORT_CONTENT_SECTION_ICON_SIZE,
                },
              }}
            >
              {icon}
            </Box>
          ) : null}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant='body2'
              color={isUnavailable ? 'text.disabled' : 'text.primary'}
              sx={{ display: 'inline-flex', alignItems: 'center', minHeight: 22 }}
            >
              {title}
            </Typography>
          </Box>
        </Box>
        {switchChecked && inlineControls && (
          <Box
            sx={(theme) => ({
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              mr: 1,
              opacity: getContentDimOpacity(
                theme,
                isUnavailable,
                switchChecked
              ),
            })}
          >
            {inlineControls}
          </Box>
        )}
        {showSwitch &&
          wrapSwitchWithUnavailableTooltip(
            intl,
            <Switch
              checked={switchChecked}
              disabled={disabled || isUnavailable}
              onChange={(_, checked) => onEnabledChange(checked)}
              sx={reportContentSwitchSx(isSwitchInteractive)}
              data-qa={dataQa ? `${dataQa}_switch` : undefined}
              inputProps={{
                'aria-label': switchAriaLabel,
              }}
            />,
            isUnavailable ? unavailableReasonId : undefined
          )}
      </Box>
      {showDetails && (
        <Collapse in={switchChecked} timeout='auto' unmountOnExit={showSwitch}>
          <Box
            sx={(theme) => ({
              ...reportListItemCollapseDetailsSx(
                theme,
                getContentDimOpacity(theme, isUnavailable, switchChecked)
              ),
              ...(isNested
                ? { gap: 0, pl: 0, pb: 0.5 }
                : isNestedContent
                  ? { gap: 0, pl: 4.75, pb: 0.5 }
                  : { gap: 1.5, pl: 4.5, pb: 1.5 }),
              pr: 0,
              minWidth: 0,
              maxWidth: '100%',
              overflow: 'hidden',
            })}
          >
            {children}
          </Box>
        </Collapse>
      )}
    </Box>
  );
}

export function ReportMessageAttachmentsStyleRow({
  attachmentsEnabled,
  onAttachmentsEnabledChange,
  displayStyle,
  onDisplayStyleChange,
  unavailable,
  unavailableReasonId,
  'data-qa': dataQa,
}: {
  attachmentsEnabled: boolean;
  onAttachmentsEnabledChange: (enabled: boolean) => void;
  displayStyle: ImageDisplayStyle;
  onDisplayStyleChange: (displayStyle: ImageDisplayStyle) => void;
  unavailable?: boolean;
  unavailableReasonId?: string;
  'data-qa'?: string;
}): ReactElement {
  const intl = useIntl();
  const isUnavailable = unavailable === true;
  const switchChecked = attachmentsEnabled && !isUnavailable;

  const switchControl = (
    <Switch
      checked={switchChecked}
      disabled={isUnavailable}
      onChange={(_, checked) => onAttachmentsEnabledChange(checked)}
      sx={reportContentSwitchSx(!isUnavailable)}
      data-qa={dataQa ? `${dataQa}_attachments_switch` : undefined}
      inputProps={{
        'aria-label': formatTranslation(intl, ReportTranslationKeys.attachmentsToggleLabel,),
      }}
    />
  );

  return (
    <Box sx={REPORT_CONTENT_SUBOPTIONS_SX}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minHeight: 40,
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
        }}
      >
        <Typography
          variant='body2'
          color={isUnavailable ? 'text.disabled' : 'text.secondary'}
          sx={{ flex: 1, minWidth: 0 }}
        >
          <FormattedMessage id={ReportTranslationKeys.attachmentsToggleLabel} />
        </Typography>
        {wrapSwitchWithUnavailableTooltip(
          intl,
          switchControl,
          isUnavailable ? unavailableReasonId : undefined
        )}
      </Box>
      {!isUnavailable && (
        <ReportSuboptionRow labelId={ReportTranslationKeys.suboptionDisplayStyle}>
          <DisplayStyleSegmentedControl
            value={displayStyle}
            onChange={onDisplayStyleChange}
            disabled={!switchChecked}
            data-qa={dataQa ? `${dataQa}_display_style` : undefined}
          />
        </ReportSuboptionRow>
      )}
    </Box>
  );
}

export function ReportAttachmentsSubsection({
  enabled,
  onEnabledChange,
  labelId,
  unavailable,
  unavailableReasonId,
  'data-qa': dataQa,
  children,
}: {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  labelId: string;
  unavailable?: boolean;
  unavailableReasonId?: string;
  'data-qa'?: string;
  children: ReactNode;
}): ReactElement {
  const intl = useIntl();
  const hasDetails = children !== undefined && children !== null;
  const isUnavailable = unavailable === true;
  const switchChecked = enabled && !isUnavailable;
  const showDetails = hasDetails && !isUnavailable;
  const isSwitchInteractive = !isUnavailable;

  return (
    <Box sx={{ width: '100%' }} data-qa={dataQa}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minHeight: 40,
        }}
      >
        <Box
          sx={(theme) => ({
            flex: 1,
            minWidth: 0,
            pr: 1,
            opacity: getContentDimOpacity(
              theme,
              isUnavailable,
              switchChecked
            ),
            transition: theme.transitions.create('opacity', {
              duration: theme.transitions.duration.shortest,
            }),
          })}
        >
          <Typography
            variant='body2'
            color={isUnavailable ? 'text.disabled' : 'text.secondary'}
          >
            <FormattedMessage id={unsafeTranslationKey(labelId)} />
          </Typography>
        </Box>
        {wrapSwitchWithUnavailableTooltip(
          intl,
          <Switch
            checked={switchChecked}
            disabled={isUnavailable}
            onChange={(_, checked) => onEnabledChange(checked)}
            sx={reportContentSwitchSx(isSwitchInteractive)}
            data-qa={dataQa ? `${dataQa}_switch` : undefined}
            inputProps={{
              'aria-label': formatTranslation(intl, unsafeTranslationKey(labelId) ),
            }}
          />,
          isUnavailable ? unavailableReasonId : undefined
        )}
      </Box>
      {showDetails && (
        <Collapse in={switchChecked} timeout='auto' unmountOnExit>
          <Box
            sx={(theme) => ({
              ...reportListItemCollapseDetailsSx(
                theme,
                getContentDimOpacity(theme, isUnavailable, switchChecked)
              ),
              pb: 0.5,
            })}
          >
            {children}
          </Box>
        </Collapse>
      )}
    </Box>
  );
}

export function ReportAdditionalInformationSectionPresentational({
  showFileNames,
  showFileDescriptions,
  showActiveLinks,
  onToggle,
  showIcon = true,
}: {
  showFileNames: boolean;
  showFileDescriptions: boolean;
  showActiveLinks: boolean;
  onToggle: (key: ReportAdditionalInformationField) => void;
  showIcon?: boolean;
}): ReactElement {
  return (
    <ReportContentListItem
      icon={showIcon ? <InfoOutlined /> : undefined}
      title={
        <FormattedMessage
          id={ReportTranslationKeys.sectionAdditionalInformation}
        />
      }
      enabled
      onEnabledChange={() => undefined}
      showSwitch={false}
      data-qa='report_section_additional_information'
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignContent: 'flex-start', gap: 1 }}>
        <ReportFilterChip
          selected={showFileNames}
          labelId={ReportTranslationKeys.fileInfoChipNames}
          tooltipId={REPORT_FILE_INFO_CHIP_TOOLTIP_LABELS.names}
          data-qa='report_additional_info_show_file_names'
          onClick={() => onToggle(ReportAdditionalInformationField.showFileNames)}
        />
        <ReportFilterChip
          selected={showFileDescriptions}
          labelId={ReportTranslationKeys.fileInfoChipDescriptions}
          tooltipId={REPORT_FILE_INFO_CHIP_TOOLTIP_LABELS.descriptions}
          data-qa='report_additional_info_show_file_descriptions'
          onClick={() =>
            onToggle(ReportAdditionalInformationField.showFileDescriptions)
          }
        />
        <ReportFilterChip
          selected={showActiveLinks}
          labelId={ReportTranslationKeys.fileInfoChipActiveLinks}
          tooltipId={REPORT_FILE_INFO_CHIP_TOOLTIP_LABELS.activeLinks}
          data-qa='report_additional_info_show_active_links'
          onClick={() => onToggle(ReportAdditionalInformationField.showActiveLinks)}
        />
      </Box>
    </ReportContentListItem>
  );
}

export function ReportEstimatedSizeBadgePresentational({
  label,
  isSplit,
}: {
  label: string;
  isSplit: boolean;
}): ReactElement {
  return (
    <Chip
      icon={
        isSplit ? (
          <CopyAllOutlined sx={{ fontSize: 16 }} />
        ) : (
          <PictureAsPdfOutlined sx={{ fontSize: 16 }} />
        )
      }
      label={label}
      variant='outlined'
      size='small'
      data-qa='report_estimated_size_badge'
      sx={{
        height: 32,
        px: 0.25,
        '& .MuiChip-label': {
          px: 0.75,
          fontSize: 13,
          fontWeight: 500,
        },
        '& .MuiChip-icon': {
          ml: 0.75,
          color: 'inherit',
        },
        ...(isSplit
          ? {
              borderColor: Color.statusDraftAmber,
              color: Color.warningOrange,
              bgcolor: Color.primaryOrangeLight,
            }
          : {
              borderColor: Color.neutralBorderGray,
              color: Color.textSecondary,
              bgcolor: 'background.paper',
            }),
      }}
    />
  );
}

export function ReportMessageIncludeInPdfCheckbox({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
}): ReactElement {
  const intl = useIntl();

  return (
    <Box
      sx={{
        ...REPORT_FORM_FULL_WIDTH_SX,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        mt: 0.5,
      }}
      data-qa='report_message_include_in_pdf'
    >
      <FormControlLabel
        sx={{ mr: 0.5 }}
        control={
          <Checkbox
            size='small'
            checked={checked}
            onChange={(event) => onChange(event.target.checked)}
            data-qa='report_message_include_in_pdf_checkbox'
            sx={{
              color: Color.textSecondary,
              '&.Mui-checked': { color: Color.primaryOrange },
            }}
          />
        }
        label={
          <Typography variant='body2' color='text.secondary'>
            <FormattedMessage
              id={ReportTranslationKeys.messageIncludeInPdfLabel}
            />
          </Typography>
        }
      />
      <Tooltip
        title={formatTranslation(intl, ReportTranslationKeys.messageIncludeInPdfTooltip,)}
        arrow
        placement='top'
      >
        <InfoOutlined
          sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }}
        />
      </Tooltip>
    </Box>
  );
}

